import React, {useEffect, useMemo, useState} from 'react';
import cn from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import IconEmailed from 'assets/feedback/icon-emailed.svg';
import IconCalled from 'assets/feedback/icon-called.svg';
import IconPhone from 'assets/feedback/icon-phone.svg';
import IconSkip from 'assets/feedback/icon-skip.svg';
import IconCopy from 'assets/feedback/icon-copy.svg';
import {ReactComponent as IconCheckOutline} from 'assets/feedback/icon-check-outline.svg';
import IconTicketPercent from 'assets/feedback/IconTicketPercent';
import {GET_FEEDBACK_REVIEW_CONTEXT} from 'store/feedback/action';
import {
  useFeedbackReviewContext,
  useFeedbackReviewContextLoading,
  useFeedbackReplySent,
  useFeedbackDiscount,
  useFeedbackDiscountLoading
} from 'store/feedback/selectors';
import {
  FEEDBACK_REPLY_TEMPLATES,
  FEEDBACK_REVIEW_STATUS,
  FEEDBACK_SENTIMENT
} from './mockData';
import {buildPromoSentence, formatMoney} from './promoUtils';
import ReplyModal from './ReplyModal';
import FeedbackDiscountFormModal from './FeedbackDiscountFormModal';
import {
  ActivePromoPill,
  ActivePromoPillCode,
  ActivePromoPillEdit,
  ActivePromoPillIcon,
  ActivePromoPillMeta,
  ActivePromoPillNote,
  ActivePromoPillText,
  ContactItem,
  ContactItemCopy,
  ContactItemIcon,
  ContactItemValue,
  ContactRow,
  CopyMessageSecondaryButton,
  CreateDiscountCTA,
  CreateDiscountCTAIcon,
  CreateDiscountCTALink,
  CreateDiscountCTAPrompt,
  CreateDiscountCTAText,
  DidYouContactInline,
  DidYouContactInlineLabel,
  DidYouContactItem,
  DidYouContactRow,
  ExpandedSectionCard,
  ExpandedSections,
  OrderContextGroup,
  OrderContextItem,
  OrderContextLabel,
  OrderContextStrip,
  OrderContextValue,
  PromoRow,
  ReplyActionsHelper,
  ReplyActionsRow,
  ReplyButton,
  ReplyMessage,
  ReplySentBanner,
  ReplySentCheck,
  ReplyTemplate,
  ReplyTemplatesRow,
  Subsection,
  SubsectionLabel
} from './styles';

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const sentimentFor = (rating) =>
  rating <= 3 ? FEEDBACK_SENTIMENT.NEGATIVE : FEEDBACK_SENTIMENT.POSITIVE;

const ExpandedCard = ({review, onContact}) => {
  const dispatch = useDispatch();
  const context = useFeedbackReviewContext(review.id);
  const contextLoading = useFeedbackReviewContextLoading(review.id);
  const replySent = useFeedbackReplySent(review.id);
  const feedbackDiscount = useFeedbackDiscount();
  const feedbackDiscountLoading = useFeedbackDiscountLoading();
  const merchantEmail = useSelector((state) => state.user?.userData?.email);
  const restaurantName = useSelector(
    (state) =>
      state.user?.activeLocation?.FullName ||
      state.user?.activeLocation?.BusinessName ||
      ''
  );

  const sentiment = sentimentFor(review.rating);
  const templates = FEEDBACK_REPLY_TEMPLATES[sentiment];
  const [activeTemplateId, setActiveTemplateId] = useState(templates[0].id);
  const [messageCopied, setMessageCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);

  useEffect(() => {
    const next = FEEDBACK_REPLY_TEMPLATES[sentimentFor(review.rating)];
    setActiveTemplateId(next[0].id);
  }, [review.id, review.rating]);

  // Fetch context the first time this card is expanded. The reducer caches
  // by id, so collapsing + re-expanding does not re-hit the network.
  useEffect(() => {
    if (!review.id || context) return;
    dispatch(GET_FEEDBACK_REVIEW_CONTEXT(review.id));
  }, [review.id, context]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === activeTemplateId) || templates[0],
    [templates, activeTemplateId]
  );

  const baseMessage = selectedTemplate.text({
    ...review,
    customerName: review.customerName,
    order: context?.order
  });
  const promoSentence = feedbackDiscountLoading
    ? null
    : buildPromoSentence(feedbackDiscount, sentiment);
  const renderedMessage = promoSentence
    ? `${baseMessage} ${promoSentence}`
    : baseMessage;

  const handleCopyMessage = async () => {
    if (!navigator.clipboard) {
      toast.error('Copy is not available in this browser');
      return;
    }
    try {
      await navigator.clipboard.writeText(renderedMessage);
    } catch {
      toast.error('Could not copy reply to clipboard');
      return;
    }
    setMessageCopied(true);
    toast.success('Reply copied to clipboard');
    setTimeout(() => setMessageCopied(false), 2000);
  };

  const handleCopyField = (field, value) => {
    if (!value) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value);
    }
    setCopiedField(field);
    setTimeout(
      () => setCopiedField((current) => (current === field ? null : current)),
      2000
    );
  };

  const {customer} = review;
  const order = context?.order;
  const contact = context?.contact;
  const fallbackText = contextLoading ? 'Loading…' : '—';

  const customerType =
    customer?.type === 'repeat' ? 'Repeat Customer' : 'New Customer';
  const ordersLabel = `${customer?.orderCount ?? 0} order${
    customer?.orderCount === 1 ? '' : 's'
  }`;
  const orderIdLine = order?.number ? `#${order.number}` : fallbackText;
  const totalLine =
    order?.total != null ? formatCurrency(order.total) : fallbackText;

  return (
    <ExpandedSections>
      <OrderContextStrip>
        <OrderContextItem>
          <OrderContextLabel>{customerType}</OrderContextLabel>
          <OrderContextValue>{ordersLabel}</OrderContextValue>
        </OrderContextItem>
        <OrderContextGroup>
          <OrderContextItem>
            <OrderContextLabel>Order ID</OrderContextLabel>
            <OrderContextValue>{orderIdLine}</OrderContextValue>
          </OrderContextItem>
          <OrderContextItem>
            <OrderContextLabel>Total</OrderContextLabel>
            <OrderContextValue>{totalLine}</OrderContextValue>
          </OrderContextItem>
        </OrderContextGroup>
      </OrderContextStrip>

      <ExpandedSectionCard>
        <Subsection $variant='gray'>
          <SubsectionLabel>Contact this customer</SubsectionLabel>
          <ContactRow>
            <ContactItem>
              <ContactItemIcon src={IconEmailed} alt='' />
              <ContactItemValue>
                {contact?.email || fallbackText}
              </ContactItemValue>
              <ContactItemCopy
                type='button'
                aria-label='Copy email'
                disabled={!contact?.email}
                onClick={() => handleCopyField('email', contact?.email)}
              >
                {copiedField === 'email' ? 'Copied' : 'Copy'}
              </ContactItemCopy>
            </ContactItem>
            <ContactItem>
              <ContactItemIcon src={IconPhone} alt='' />
              <ContactItemValue>
                {contact?.phone || fallbackText}
              </ContactItemValue>
              <ContactItemCopy
                type='button'
                aria-label='Copy phone'
                disabled={!contact?.phone}
                onClick={() => handleCopyField('phone', contact?.phone)}
              >
                {copiedField === 'phone' ? 'Copied' : 'Copy'}
              </ContactItemCopy>
            </ContactItem>
          </ContactRow>

          <SubsectionLabel>Suggested reply</SubsectionLabel>
          <ReplyTemplatesRow>
            {templates.map((template) => (
              <ReplyTemplate
                key={template.id}
                type='button'
                data-label={template.label}
                className={cn({isActive: template.id === selectedTemplate.id})}
                onClick={() => setActiveTemplateId(template.id)}
              >
                {template.label}
              </ReplyTemplate>
            ))}
          </ReplyTemplatesRow>
        </Subsection>

        <Subsection $variant='white' $borderTop>
          <ReplyMessage>{renderedMessage}</ReplyMessage>
          <PromoRow>
            {!feedbackDiscountLoading && feedbackDiscount && (
              <ActivePromoPill>
                <ActivePromoPillIcon>
                  <IconTicketPercent />
                </ActivePromoPillIcon>
                <ActivePromoPillText>
                  <ActivePromoPillCode>
                    {feedbackDiscount.promoCode?.code}
                  </ActivePromoPillCode>
                  <ActivePromoPillMeta>
                    ${formatMoney(feedbackDiscount.rate?.value)} off, min $
                    {formatMoney(feedbackDiscount.subtotal?.min)}
                  </ActivePromoPillMeta>
                  <ActivePromoPillNote>
                    Now included in all replies
                  </ActivePromoPillNote>
                </ActivePromoPillText>
                <ActivePromoPillEdit
                  type='button'
                  onClick={() => setDiscountModalOpen(true)}
                >
                  Edit
                </ActivePromoPillEdit>
              </ActivePromoPill>
            )}
            {!feedbackDiscountLoading && !feedbackDiscount && (
              <CreateDiscountCTA
                type='button'
                onClick={() => setDiscountModalOpen(true)}
              >
                <CreateDiscountCTAIcon>
                  <IconTicketPercent width={16} height={16} />
                </CreateDiscountCTAIcon>
                <CreateDiscountCTAText>
                  <CreateDiscountCTAPrompt>
                    Want to include a discount in your replies?
                  </CreateDiscountCTAPrompt>
                  <CreateDiscountCTALink>
                    Create a Promo Discount &rarr;
                  </CreateDiscountCTALink>
                </CreateDiscountCTAText>
              </CreateDiscountCTA>
            )}
          </PromoRow>
          <ReplyActionsRow>
            <ReplyButton
              type='button'
              disabled={!contact?.email}
              onClick={() => setReplyModalOpen(true)}
            >
              Reply
            </ReplyButton>
            <CopyMessageSecondaryButton
              type='button'
              disabled={messageCopied}
              onClick={handleCopyMessage}
            >
              <img src={IconCopy} alt='' />
              {messageCopied ? 'Copied!' : 'Copy message'}
            </CopyMessageSecondaryButton>
            {merchantEmail && (
              <ReplyActionsHelper>
                {`Sends from ${merchantEmail}`}
              </ReplyActionsHelper>
            )}
          </ReplyActionsRow>
        </Subsection>

        {replySent ? (
          <ReplySentBanner>
            <ReplySentCheck aria-hidden='true'>
              <IconCheckOutline />
            </ReplySentCheck>
            <span>
              <strong>Reply sent</strong>
              {` to ${replySent.customerEmail} · ${moment(
                replySent.sentAt
              ).format('MMM D [at] h:mm A')}`}
            </span>
          </ReplySentBanner>
        ) : (
          <Subsection $variant='white' $borderTop>
            <DidYouContactInline>
              <DidYouContactInlineLabel>
                Did you contact this customer?
              </DidYouContactInlineLabel>
              <DidYouContactRow>
                <DidYouContactItem
                  type='button'
                  onClick={() =>
                    onContact(review.id, FEEDBACK_REVIEW_STATUS.EMAILED)
                  }
                >
                  <img src={IconEmailed} alt='' />
                  Emailed
                </DidYouContactItem>
                <DidYouContactItem
                  type='button'
                  onClick={() =>
                    onContact(review.id, FEEDBACK_REVIEW_STATUS.CALLED)
                  }
                >
                  <img src={IconCalled} alt='' />
                  Called
                </DidYouContactItem>
                <DidYouContactItem
                  type='button'
                  onClick={() =>
                    onContact(review.id, FEEDBACK_REVIEW_STATUS.SKIPPED)
                  }
                >
                  <img src={IconSkip} alt='' />
                  Skip
                </DidYouContactItem>
              </DidYouContactRow>
            </DidYouContactInline>
          </Subsection>
        )}
      </ExpandedSectionCard>

      <ReplyModal
        open={replyModalOpen}
        onClose={() => setReplyModalOpen(false)}
        review={{...review, contact}}
        restaurantName={restaurantName}
        orderItems={order?.items}
      />

      <FeedbackDiscountFormModal
        open={discountModalOpen}
        discount={feedbackDiscount}
        onClose={() => setDiscountModalOpen(false)}
      />
    </ExpandedSections>
  );
};

ExpandedCard.propTypes = {
  review: PropTypes.object.isRequired,
  onContact: PropTypes.func
};

ExpandedCard.defaultProps = {
  onContact: () => {}
};

export default ExpandedCard;
